import { TimezoneHelper } from '@shared/helpers';
import { FeeWaiverDateInterval } from '@shared/types/waivers';
import moment from 'moment';

const US_CENTRAL_TZ = 'America/Chicago';

function computePayloadStartDate(date: Date): Date {
    const year = date.getFullYear();
    const month = date.getMonth();

    return moment.tz({ year, month, day: 1, hour: 0, minute: 0, second: 0 }, US_CENTRAL_TZ).utc().toDate();
}

function computePayloadEndDate(date: Date): Date {
    const year = date.getFullYear();
    const month = date.getMonth();
    const nextYear = month === 11 ? year + 1 : year;
    const nextMonth = month === 11 ? 0 : month + 1;

    return moment
        .tz({ year: nextYear, month: nextMonth, day: 1, hour: 0, minute: 0, second: 0 }, US_CENTRAL_TZ)
        .utc()
        .toDate();
}

function readResponseStartDate(date: string): Date {
    const d = moment.tz(date, US_CENTRAL_TZ);
    return new Date(d.year(), d.month(), d.date());
}

function readResponseEndDate(date: string): Date {
    const d = moment.tz(date, US_CENTRAL_TZ).subtract(1, 'day').startOf('month');
    return new Date(d.year(), d.month(), d.date());
}

function formatWaiverDisplayDate(date: Date | null): string {
    if (!date) {
        return '';
    }
    return moment({ year: date.getFullYear(), month: date.getMonth(), date: date.getDate() }).format('MMM YYYY');
}

function formatWaiverDisplayFullDate(date: Date | null): string {
    if (!date) {
        return '';
    }
    return moment({ year: date.getFullYear(), month: date.getMonth(), date: date.getDate() }).format('MMMM Do, YYYY');
}

function firstDayOfCurrentBillingMonth(): Date {
    const now = moment.tz(US_CENTRAL_TZ);
    return new Date(now.year(), now.month(), 1);
}

function lastDayOfCurrentBillingMonth(): Date {
    const now = moment.tz(US_CENTRAL_TZ);
    return new Date(now.year(), now.month() + 1, 0);
}

function readAndFormatResponseStartDate(date: string): string {
    const convertedDate = readResponseStartDate(date);
    return formatWaiverDisplayDate(convertedDate);
}

function readAndFormatResponseEndDate(date: string): string {
    const convertedDate = readResponseEndDate(date);
    return formatWaiverDisplayDate(convertedDate);
}

function calculateNumberOfMonthsInInterval(start: Date | null, end: Date | null): number {
    if (!start || !end) {
        return 0;
    }

    const startYear = start.getFullYear();
    const endYear = end.getFullYear();
    const startMonth = start.getMonth();
    const endMonth = end.getMonth();

    let counter = 0;

    if (endYear > startYear) {
        const multiply = endYear - startYear;
        counter = 12 * multiply;

        if (endMonth > startMonth) {
            counter += endMonth - startMonth + 1;
        } else if (endMonth < startMonth) {
            counter -= startMonth - endMonth - 1;
        } else {
            counter += 1;
        }

        return counter;
    }

    if (endMonth > startMonth) {
        counter = endMonth - startMonth + 1;
    } else {
        counter = 1;
    }

    return counter;
}

/** Inclusive month count from start through min(reference, end), for active-waiver progress (e.g. 2/13). */
function calculateNumberOfMonthsElapsedInInterval(
    start: Date | null,
    end: Date | null,
    reference: Date = new Date()
): number {
    if (!start || !end) {
        return 0;
    }
    const endCap = reference.getTime() < end.getTime() ? reference : end;
    if (endCap.getTime() < start.getTime()) {
        return 0;
    }
    return calculateNumberOfMonthsInInterval(start, endCap);
}

function buildFeeWaiverDateInterval(start: Date, end: Date): FeeWaiverDateInterval {
    return {
        startMonthYear: start.toLocaleString('default', { month: 'long', year: 'numeric' }),
        endMonthYear: end.toLocaleString('default', { month: 'long', year: 'numeric' }),
        count: calculateNumberOfMonthsInInterval(start, end),
    };
}

function isExpiringWaiverEndDateUpdated(loaded: Date | null, current: Date | null, currentMonthRef: Date): boolean {
    if (!loaded || !current) {
        return false;
    }

    const isSameCalendarDay =
        loaded.getFullYear() === current.getFullYear() &&
        loaded.getMonth() === current.getMonth() &&
        loaded.getDate() === current.getDate();

    if (isSameCalendarDay) {
        return false;
    }
    const priorToRef = new Date(currentMonthRef);
    priorToRef.setMonth(priorToRef.getMonth() - 1);
    return current.getFullYear() === priorToRef.getFullYear() && current.getMonth() === priorToRef.getMonth();
}

function generateEndDateBasedOn15thOfMonth(): Date {
    const now = new Date();
    const monthsToAdd = now.getUTCDate() < 15 ? 2 : 3;

    return new Date(now.getUTCFullYear(), now.getUTCMonth() + monthsToAdd, 1);
}

function isOngoingWaiver(startDate: Date | null): boolean {
    if (!startDate) {
        return false;
    }

    return TimezoneHelper.firstDayOfMonth(startDate) < TimezoneHelper.firstDayOfCurrentUTCMonth();
}

export const FeeWaiverDatesHelper = {
    computePayloadStartDate,
    computePayloadEndDate,
    readResponseStartDate,
    readResponseEndDate,
    formatWaiverDisplayDate,
    formatWaiverDisplayFullDate,
    firstDayOfCurrentBillingMonth,
    lastDayOfCurrentBillingMonth,
    readAndFormatResponseStartDate,
    readAndFormatResponseEndDate,
    calculateNumberOfMonthsInInterval,
    calculateNumberOfMonthsElapsedInInterval,
    buildFeeWaiverDateInterval,
    isExpiringWaiverEndDateUpdated,
    generateEndDateBasedOn15thOfMonth,
    isOngoingWaiver,
};
